// src/pages/OrderOnTheWay/components/OrderTableWithAccordion.jsx
import React, { useState, useMemo } from 'react';
import { calculateOrderSettlement } from '../utils/calculationutil';

const tableHeaderStyle = {
  border: '1px solid #1f2937',
  padding: '4px 6px',
  textAlign: 'left',
  fontSize: '11px',
  fontWeight: 'bold',
  backgroundColor: '#f3f4f6',
  color: '#4b5563',
};

const tableCellStyle = {
  border: '1px solid #1f2937',
  padding: '4px 6px',
  fontSize: '12px',
};

const OrderTableWithAccordion = ({ 
  orders, 
  showJmiDetails = false, 
  showAllSettlements = false, 
  showFineDetails = false,
  groupBy = 'buyer', // 'buyer', 'seller', or 'status'
  searchTerm = '',
  statusFilter = 'all'
}) => {
  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  const formatDate = (date) => {
    if (!date) return '—';
    try {
      const d = date.toDate ? date.toDate() : new Date(date);
      return d.toLocaleString('en-IN');
    } catch {
      return '—';
    }
  };

  const getStatusStyle = (status) => ({
    display: 'inline-block',
    padding: '2px 6px',
    fontSize: '11px',
    fontWeight: 'bold',
    borderRadius: '4px',
    textTransform: 'capitalize',
    backgroundColor:
      status === 'Requested' ? '#fef3c7' :
      status === 'Accepted' ? '#dcfce7' :
      status === 'Assorted' ? '#ede9fe' :
      status === 'FinalCorrection' ? '#e0e7ff' :
      status === 'Delivered' ? '#d1fae5' :
      status === 'paymentDeliveredToSeller' ? '#d1fae5' :
      status === 'On The Way' ? '#fef3c7' : '#e5e7eb',
    color:
      status === 'Requested' ? '#92400e' :
      status === 'Accepted' ? '#166534' :
      status === 'Assorted' ? '#5b21b6' :
      status === 'FinalCorrection' ? '#3730a3' :
      status === 'Delivered' ? '#065f46' :
      status === 'paymentDeliveredToSeller' ? '#065f46' :
      status === 'On The Way' ? '#92400e' : '#374151'
  });

  // Calculate totals for a group
  const calculateGroupTotals = (groupOrders) => {
    return groupOrders.reduce((acc, order) => {
      const settlement = calculateOrderSettlement(order);
      return {
        totalOrders: acc.totalOrders + 1,
        // Settlement totals already account for quantity; order.netWt is per-set.
        totalNetWt: acc.totalNetWt + (settlement.TotalnetWt || parseFloat(order.netWt) || 0),
        totalGrossWt: acc.totalGrossWt + (settlement.TotalgrossWt || parseFloat(order.grossWt) || 0),
        totalAmount: acc.totalAmount + settlement.totalAmount,
        jmiFine: acc.jmiFine + settlement.jmiFine,
        jmiAmount: acc.jmiAmount + settlement.totalAmountJmi,
        sellerAmount: acc.sellerAmount + settlement.totalAmountSeller,
        operatorAmount: acc.operatorAmount + settlement.totalAmountOperator,
        sellerFine: acc.sellerFine + settlement.sellerFine,
        fineWeight: acc.fineWeight + settlement.fineWeight,
        totalQty: acc.totalQty + (parseFloat(order.totalQty) || 0)
      };
    }, {
      totalOrders: 0,
      totalNetWt: 0,
      totalGrossWt: 0,
      totalAmount: 0,
      jmiFine: 0,
      jmiAmount: 0,
      sellerAmount: 0,
      operatorAmount: 0,
      sellerFine: 0,
      fineWeight: 0,
      totalQty: 0
    });
  };

  // Filter and group orders
  const groupedData = useMemo(() => {
    let filteredOrders = orders;

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase().trim();
      filteredOrders = filteredOrders.filter(order =>
        (order.buyerId || '').toLowerCase().includes(search) ||
        (order.sellerId || '').toLowerCase().includes(search) ||
        (order.id || '').toLowerCase().includes(search) ||
        (order.productName || '').toLowerCase().includes(search) ||
        (order.operatorId || '').toLowerCase().includes(search)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filteredOrders = filteredOrders.filter(order =>
        (order.orderStatus || '').toLowerCase() === statusFilter.toLowerCase()
      );
    }

    // Group orders
    const groups = {};
    filteredOrders.forEach(order => {
      let groupKey;
      switch(groupBy) {
        case 'seller':
          groupKey = order.sellerId || 'Unknown Seller';
          break;
        case 'status':
          groupKey = order.orderStatus || 'Unknown';
          break;
        case 'buyer':
        default:
          groupKey = order.buyerId || 'Unknown Buyer';
          break;
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(order);
    });

    // Sort groups by key
    const sortedGroups = Object.keys(groups).sort();
    const result = {};
    sortedGroups.forEach(key => {
      result[key] = groups[key];
    });

    return result;
  }, [orders, searchTerm, statusFilter, groupBy]);

  // Get size rows for an order (for detailed view)
  const toNum = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };

  /**
   * Per-size rows for the product card.
   *
   * `orderDetail` (written by the seller's Final Correction) is the settlement
   * basis: it stores PER-SET averages plus a quantity, e.g.
   *   { "10": { qty: 3, grossWT: 6.2, netwt: 5.1, specWT: 1.1 } }
   * so each row now exposes the per-set figure AND the line total
   * (per-set x qty). Previously only the per-set value was returned, which made
   * a 3-set row read as 5.100g instead of 15.300g. Variants / lot details are
   * only used when there is no orderDetail.
   */
  const getSizeRows = (order) => {
    const detail = order.orderDetail;
    const hasDetail =
      detail && typeof detail === 'object' && Object.keys(detail).length > 0;

    const variants = order.selectedVariants || [];
    const lots = order.lotDetails || [];
    const lotBySize = {};
    lots.forEach((l) => {
      if (l.size != null) lotBySize[l.size] = l;
    });

    const raw = [];

    if (hasDetail) {
      Object.entries(detail).forEach(([size, d]) => {
        const qty = toNum(d.qty ?? d.set ?? d.quantity) || 1;
        const netPerSet = toNum(d.netwt ?? d.netWt);
        const grossPerSet = toNum(d.grossWT ?? d.grossWt);
        const specPerSet =
          d.specWT ?? d.specWt ?? Math.max(0, grossPerSet - netPerSet);
        raw.push({ size, qty, netPerSet, grossPerSet, specPerSet });
      });
    } else if (variants.length) {
      variants.forEach((v) => {
        const lot = lotBySize[v.size];
        const qty = toNum(v.quantity) || 1;
        const netPerSet = toNum(lot?.netWt ?? lot?.avgNetWt ?? order.netWt);
        const grossPerSet = toNum(lot?.grossWt ?? lot?.avgGrossWt ?? order.grossWt);
        raw.push({ size: v.size, qty, netPerSet, grossPerSet, specPerSet: null });
      });
    } else if (lots.length) {
      lots.forEach((l) => {
        const qty = toNum(l.set ?? l.qty) || 1;
        const netPerSet = toNum(l.netWt ?? l.avgNetWt ?? order.netWt);
        const grossPerSet = toNum(l.grossWt ?? l.avgGrossWt ?? order.grossWt);
        raw.push({ size: l.size, qty, netPerSet, grossPerSet, specPerSet: null });
      });
    }

    return raw.map((r) => {
      const specPerSet =
        r.specPerSet == null
          ? Math.max(0, r.grossPerSet - r.netPerSet)
          : toNum(r.specPerSet);
      return {
        size: r.size,
        qty: r.qty,
        netPerSet: r.netPerSet,
        grossPerSet: r.grossPerSet,
        specPerSet,
        // Line totals — these are what roll up into the order's gross/net.
        netWt: r.netPerSet * r.qty,
        grossWt: r.grossPerSet * r.qty,
        specWt: specPerSet * r.qty,
      };
    });
  };

  if (Object.keys(groupedData).length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '32px',
        color: '#6b7280',
        fontSize: '16px'
      }}>
        No orders found matching the criteria.
      </div>
    );
  }

  return (
    <div style={{ padding: '8px 0', overflowX: 'auto' }}>
      {Object.entries(groupedData).map(([groupKey, groupOrders]) => {
        const isExpanded = expandedGroups[groupKey];
        const totals = calculateGroupTotals(groupOrders);

        return (
          <div 
            key={groupKey} 
            style={{ 
              marginBottom: '12px', 
              border: '1px solid #1f2937', 
              borderRadius: '6px', 
              overflow: 'hidden' 
            }}
          >
            {/* Group Summary - Clickable Accordion Header */}
            <div 
              onClick={() => toggleGroup(groupKey)}
              style={{ 
                backgroundColor: '#1f2937', 
                color: '#ffffff', 
                padding: '8px 12px', 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '10px', 
                alignItems: 'center',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#374151'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
            >
              <strong style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>{isExpanded ? '▼' : '▶'}</span>
                {groupBy === 'buyer' ? 'Buyer:' :
                 groupBy === 'seller' ? 'Seller:' :
                 'Status:'} {groupKey}
              </strong>
              <span>Orders: {totals.totalOrders}</span>
              <span>Qty: {totals.totalQty.toFixed(2)}</span>
              <span>Gross: {totals.totalGrossWt.toFixed(3)}g</span>
              <span>Net: {totals.totalNetWt.toFixed(3)}g</span>
              <span>Amount: ₹{totals.totalAmount.toFixed(2)}</span>
              {showJmiDetails && (
                <>
                  <span>JMI Fine: ₹{totals.jmiFine.toFixed(2)}</span>
                  <span>JMI Amount: ₹{totals.jmiAmount.toFixed(2)}</span>
                </>
              )}
              {showAllSettlements && (
                <>
                  <span>Seller Amt: ₹{totals.sellerAmount.toFixed(2)}</span>
                  <span>Operator Amt: ₹{totals.operatorAmount.toFixed(2)}</span>
                </>
              )}
              {showFineDetails && (
                <>
                  <span>Fine Wt: {totals.fineWeight.toFixed(3)}g</span>
                  <span>Seller Fine: ₹{totals.sellerFine.toFixed(2)}</span>
                </>
              )}
            </div>

            {/* Accordion Content - Orders Table */}
            {isExpanded && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ minWidth: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={tableHeaderStyle}>Image</th>
                      <th style={tableHeaderStyle}>Order ID</th>
                      <th style={tableHeaderStyle}>Product</th>
                      <th style={tableHeaderStyle}>Size</th>
                      <th style={tableHeaderStyle}>Qty</th>
                      <th style={tableHeaderStyle}>
                        Net Wt (g){' '}
                        <span style={{ fontWeight: 400, color: '#6b7280' }}>total</span>
                      </th>
                      <th style={tableHeaderStyle}>
                        Gross Wt (g){' '}
                        <span style={{ fontWeight: 400, color: '#6b7280' }}>total</span>
                      </th>
                      <th style={tableHeaderStyle}>Total Amount (₹)</th>
                      {showFineDetails && <th style={tableHeaderStyle}>Fine Wt (g)</th>}
                      {showFineDetails && <th style={tableHeaderStyle}>Seller Fine (₹)</th>}
                      {showJmiDetails && <th style={tableHeaderStyle}>JMI Fine (₹)</th>}
                      {showJmiDetails && <th style={tableHeaderStyle}>JMI Amount (₹)</th>}
                      {showAllSettlements && <th style={tableHeaderStyle}>Seller Amt (₹)</th>}
                      {showAllSettlements && <th style={tableHeaderStyle}>Operator Amt (₹)</th>}
                      <th style={tableHeaderStyle}>Status</th>
                      <th style={tableHeaderStyle}>Order Time</th>
                      <th style={tableHeaderStyle}>Age</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupOrders.map((order, index) => {
                      const settlement = calculateOrderSettlement(order);
                      const sizeRows = getSizeRows(order);
                      const isOverdue = order.isOverdue;
                      const baseBg = isOverdue ? '#fee2e2' : (index % 2 === 0 ? '#ffffff' : '#f9fafb');

return (
                          <React.Fragment key={order.id || order.docId || index}>
                            {/* Main Order Row */}
                            <tr style={{ 
                              backgroundColor: baseBg, 
                              borderBottom: '1px solid #e5e7eb' 
                            }}>
                              <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                                {order.images?.[0]?.url ? (
                                  <img
                                    src={order.images[0].url.trim()}
                                    alt="Product"
                                    style={{ 
                                      height: '24px', 
                                      width: '24px', 
                                      objectFit: 'contain', 
                                      border: '1px solid #d1d5db', 
                                      borderRadius: '3px' 
                                    }}
                                  />
                                ) : (
                                  <span style={{ color: '#9ca3af' }}>—</span>
                                )}
                              </td>
                              <td style={{ ...tableCellStyle, fontFamily: 'monospace', fontSize: '11px' }}>
                                <div>{order.id || order.docId || '—'}</div>
                                {(order.sellerSettlementId || order.settlementId) && (
                                  <div style={{ fontSize: '9px', color: '#4f46e5', marginTop: '1px', fontWeight: 'bold' }}>
                                    STL: {order.sellerSettlementId || order.settlementId}
                                  </div>
                                )}
                              </td>
                              <td style={{ ...tableCellStyle, fontWeight: 'bold', fontSize: '12px' }}>
                                {order.productName || '—'}
                              </td>
                              <td style={{ ...tableCellStyle, fontSize: '11px' }}>
                                {sizeRows.length ? sizeRows.map((r, i) => (
                                  <div key={i}>{r.size}</div>
                                )) : '—'}
                              </td>
                              <td style={{ ...tableCellStyle, fontSize: '11px' }}>
                                {sizeRows.length ? sizeRows.reduce((s, r) => s + (parseFloat(r.qty) || 0), 0).toFixed(2) : '—'}
                              </td>
                              <td style={{ ...tableCellStyle, fontSize: '11px' }}>
                                {/* Prefer the settlement total (per-set x qty). order.netWt is a
                                    single per-set value and used to shadow the real total. */}
                                {settlement.TotalnetWt?.toFixed(3) ||
                                  (order.netWt ? parseFloat(order.netWt).toFixed(3) : '—')}
                              </td>
                              <td style={{ ...tableCellStyle, fontSize: '11px' }}>
                                {settlement.TotalgrossWt?.toFixed(3) ||
                                  (order.grossWt ? parseFloat(order.grossWt).toFixed(3) : '—')}
                              </td>
                              <td style={{ ...tableCellStyle, fontWeight: '600', color: '#1f2937', fontSize: '12px' }}>
                                ₹{settlement.totalAmount.toFixed(2)}
                              </td>
                              {showFineDetails && (
                                <td style={{ ...tableCellStyle, fontWeight: '600', color: '#4b5563', fontSize: '12px' }}>
                                  {settlement.fineWeight.toFixed(3)}g
                                </td>
                              )}
                              {showFineDetails && (
                                <td style={{ ...tableCellStyle, fontWeight: '600', color: '#dc2626', fontSize: '12px' }}>
                                  ₹{settlement.sellerFine.toFixed(2)}
                                </td>
                              )}
                              {showJmiDetails && (
                                <td style={{ ...tableCellStyle, fontWeight: '600', color: '#dc2626', fontSize: '12px' }}>
                                  ₹{settlement.jmiFine.toFixed(2)}
                                </td>
                              )}
                              {showJmiDetails && (
                                <td style={{ ...tableCellStyle, fontWeight: '600', color: '#059669', fontSize: '12px' }}>
                                  ₹{settlement.totalAmountJmi.toFixed(2)}
                                </td>
                              )}
                              {showAllSettlements && (
                                <td style={{ ...tableCellStyle, fontWeight: '600', color: '#3b82f6', fontSize: '12px' }}>
                                  ₹{settlement.totalAmountSeller.toFixed(2)}
                                </td>
                              )}
                              {showAllSettlements && (
                                <td style={{ ...tableCellStyle, fontWeight: '600', color: '#f59e0b', fontSize: '12px' }}>
                                  ₹{settlement.totalAmountOperator.toFixed(2)}
                                </td>
                              )}
                              <td style={tableCellStyle}>
                                <span style={getStatusStyle(order.orderStatus)}>
                                  {order.orderStatus || 'Unknown'}
                                </span>
                              </td>
                              <td style={{ ...tableCellStyle, fontSize: '10px' }}>
                                {formatDate(order.orderTime)}
                              </td>
                              <td style={{ ...tableCellStyle, fontFamily: 'monospace', fontSize: '10px' }}>
                                {order.orderTime ? (() => {
                                  const diffMs = new Date() - new Date(order.orderTime);
                                  const mins = Math.floor(diffMs / 60000);
                                  const secs = Math.floor((diffMs % 60000) / 1000);
                                  return `${mins}m ${secs}s`;
                                })() : '—'}
                              </td>
                            </tr>

                          {/* Size-wise detailing sub-rows */}
                          {sizeRows.map((r, i) => (
                            <tr 
                              key={`${order.id || order.docId || index}-${i}`} 
                              style={{ 
                                backgroundColor: '#fafafa', 
                                borderBottom: '1px solid #eef2f7',
                                fontSize: '11px' 
                              }}
                            >
                              <td style={{ ...tableCellStyle, border: 'none', padding: '2px 6px' }}></td>
                              <td style={{ ...tableCellStyle, border: 'none', padding: '2px 6px' }}></td>
                              <td style={{ ...tableCellStyle, border: 'none', paddingLeft: '16px', color: '#6b7280', fontSize: '11px' }} colSpan={2}>
                                ↳ {r.size}
                              </td>
                              <td style={{ ...tableCellStyle, border: 'none', color: '#6b7280', fontSize: '11px', padding: '2px 6px' }}>
                                {r.qty}
                              </td>
                              <td style={{ ...tableCellStyle, border: 'none', color: '#6b7280', fontSize: '10px', padding: '2px 6px', lineHeight: 1.3 }}>
                                <div>{r.netPerSet.toFixed(3)} /set</div>
                                <div style={{ fontWeight: 700, color: '#1f2937' }}>
                                  {(r.netWt || 0).toFixed(3)}g total
                                </div>
                              </td>
                              <td style={{ ...tableCellStyle, border: 'none', color: '#6b7280', fontSize: '10px', padding: '2px 6px', lineHeight: 1.3 }}>
                                <div>{r.grossPerSet.toFixed(3)} /set</div>
                                <div style={{ fontWeight: 700, color: '#1f2937' }}>
                                  {(r.grossWt || 0).toFixed(3)}g total
                                </div>
                              </td>
                              <td style={{ ...tableCellStyle, border: 'none' }} colSpan={showFineDetails ? 2 : 0}></td>
                              {showFineDetails && <td style={{ ...tableCellStyle, border: 'none' }}></td>}
                              {showFineDetails && <td style={{ ...tableCellStyle, border: 'none' }}></td>}
                              {showJmiDetails && <td style={{ ...tableCellStyle, border: 'none' }}></td>}
                              {showJmiDetails && <td style={{ ...tableCellStyle, border: 'none' }}></td>}
                              {showAllSettlements && <td style={{ ...tableCellStyle, border: 'none' }}></td>}
                              {showAllSettlements && <td style={{ ...tableCellStyle, border: 'none' }}></td>}
                              <td style={{ ...tableCellStyle, border: 'none' }} colSpan={3}></td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}

                    {/* Group Totals Row */}
                    <tr style={{ backgroundColor: '#dbeafe', fontWeight: 'bold' }}>
                      <td style={{ ...tableCellStyle, fontWeight: 'bold' }} colSpan={4}>
                        Total ({groupKey})
                      </td>
                      <td style={{ ...tableCellStyle, fontWeight: 'bold' }}>
                        {totals.totalQty.toFixed(2)}
                      </td>
                      <td style={{ ...tableCellStyle, fontWeight: 'bold' }}>
                        {totals.totalNetWt.toFixed(3)}g
                      </td>
                      <td style={{ ...tableCellStyle, fontWeight: 'bold' }}>
                        {totals.totalGrossWt.toFixed(3)}g
                      </td>
                      <td style={{ ...tableCellStyle, fontWeight: 'bold' }}>
                        ₹{totals.totalAmount.toFixed(2)}
                      </td>
                      {showFineDetails && (
                        <td style={{ ...tableCellStyle, fontWeight: 'bold' }}>
                          {totals.fineWeight.toFixed(3)}g
                        </td>
                      )}
                      {showFineDetails && (
                        <td style={{ ...tableCellStyle, fontWeight: 'bold', color: '#dc2626' }}>
                          ₹{totals.sellerFine.toFixed(2)}
                        </td>
                      )}
                      {showJmiDetails && (
                        <td style={{ ...tableCellStyle, fontWeight: 'bold', color: '#dc2626' }}>
                          ₹{totals.jmiFine.toFixed(2)}
                        </td>
                      )}
                      {showJmiDetails && (
                        <td style={{ ...tableCellStyle, fontWeight: 'bold', color: '#059669' }}>
                          ₹{totals.jmiAmount.toFixed(2)}
                        </td>
                      )}
                      {showAllSettlements && (
                        <td style={{ ...tableCellStyle, fontWeight: 'bold', color: '#3b82f6' }}>
                          ₹{totals.sellerAmount.toFixed(2)}
                        </td>
                      )}
                      {showAllSettlements && (
                        <td style={{ ...tableCellStyle, fontWeight: 'bold', color: '#f59e0b' }}>
                          ₹{totals.operatorAmount.toFixed(2)}
                        </td>
                      )}
                      <td style={{ ...tableCellStyle, fontWeight: 'bold' }} colSpan={3}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default OrderTableWithAccordion;